import { ChangeDetectorRef, Directive, HostBinding, Input, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';

import { Screen, WindowService } from '../services/window.service';

export interface RangeQuery {
  [key: string]: string;
}

const SIZE_MAP: { [key: string]: Screen } = {
  xxs: Screen.XXS,
  xs: Screen.XS,
  sm: Screen.SM,
  md: Screen.MD,
  lg: Screen.LG,
  xl: Screen.XL,
};
const VIEWPORT_MAP: { [key in Screen]?: string } = {
  [Screen.XXS]: 'xxs',
  [Screen.XS]: 'xs',
  [Screen.SM]: 'sm',
  [Screen.MD]: 'md',
  [Screen.LG]: 'lg',
  [Screen.XL]: 'xl',
};
@Directive({ selector: '[mbScreenClass]' })
export class ScreenDirective implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private _ws = inject(WindowService);

  private lastList = '';
  private subscribed!: Subscription;

  private screenSet: { [key: string]: string[] } = {};
  private defaultClass: string[] = [];

  protected _elementClass: string[] = [];

  @HostBinding('class')
  get elementClass(): string {
    return this._elementClass.join(' ');
  }

  @Input()
  set mbScreenClass(value: RangeQuery) {
    if (!value) {
      value = {};
    }
    // reset viewport class lists
    if (Object.keys(value).length) {
      const { screenSet, defaultClass } = this.generateSetObject(value);
      this.defaultClass = defaultClass;
      this.screenSet = screenSet;
      this.registerEvent();
    } else {
      this.unregisterEvent();
    }
  }

  private generateSetObject(value: RangeQuery): { screenSet: { [key: string]: string[] }; defaultClass: string[] } {
    let firstKey: string | undefined;
    const screenSet: { [key: string]: string[] } = {};
    // create new set for any size
    let defaultClass: string[] = [];
    for (const i in value) {
      if (value?.hasOwnProperty(i)) {
        if (i === 'default') {
          defaultClass = value['default'].split(' ');
          continue;
        }
        i.split('-').forEach((e) => {
          if (!firstKey) {
            firstKey = e;
          } else {
            for (const s in SIZE_MAP) {
              if (SIZE_MAP[s] > SIZE_MAP[firstKey] && SIZE_MAP[s] < SIZE_MAP[e]) {
                screenSet[s] = value[i].split(' ');
              }
            }
          }

          screenSet[e] = value[i].split(' ');
        });
        firstKey = undefined;
      }
    }
    return { screenSet, defaultClass };
  }

  private updateHostClasses(classList: string[]): void {
    const currentListString = classList.join();
    if (this.lastList === currentListString) {
      return;
    }
    this.lastList = currentListString;
    this._elementClass = classList;
    this.cdr.markForCheck();
  }

  private registerEvent() {
    if (this.subscribed) {
      return;
    }
    this.subscribed = this._ws.screen$.subscribe((currentScreen) => {
      const currentViewport = VIEWPORT_MAP[currentScreen];
      const viewportClasses: string[] = currentViewport
        ? this.screenSet[currentViewport] || this.defaultClass
        : this.defaultClass;
      this.updateHostClasses(viewportClasses);
    });
  }

  unregisterEvent() {
    if (this.subscribed) {
      this.subscribed.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.unregisterEvent();
  }
}
